import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { LocalDate } from '@js-joda/core';
import { IconActivity, Modal, ModalBody, ModalHeader } from '@americanexpress/dls-react';

import { getCategoryTransactionsUrl, goToBudgetCategoryTransactions } from 'src/services/router.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import FormatService from 'src/services/format.service';
import DateUtils from 'src/utils/date';
import Selectors from 'src/utils/selectors';
import { useSpendingHistory } from 'src/hooks/queries/spending';
import { useCategories } from 'src/hooks/queries/categories';

import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import SpendingHistoryBarChart from 'src/components/atomic/molecules/spendingHistoryBarChart/SpendingHistoryBarChart';
import Output from 'src/components/atomic/molecules/output/Output';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

import styles from './categoryHistoryModal.module.scss';
import { TooltipPlacement } from 'src/types/tooltip-placement.enum';
import { getCategoryName } from 'src/utils/category.utils';

interface Props {
    groupId: string;
    closeModal: () => void;
}

export default function CategoryHistoryModal({ groupId, closeModal }: Props): JSX.Element {
    const history = useHistory();
    const location = useLocation();
    const { data: categories } = useCategories();
    const startDate: LocalDate = DateUtils.startDateOfThisMonth().minusYears(1);
    const endDate: LocalDate = DateUtils.startDateOfThisMonth();
    const { isLoading: isLoadingSpendingHistory, data: spendingHistory } = useSpendingHistory({
        id: groupId,
        startDate,
        endDate
    });

    const handleOpenTransactions = (startDate: LocalDate, endDate: LocalDate): void =>
        goToBudgetCategoryTransactions(false, history, groupId, startDate, endDate);

    const categoryName: string = getCategoryName(groupId, categories);
    const averageSpent: string = FormatService.formatCurrency(spendingHistory?.[0]?.averageSpent, { round: true });

    return (
        // Override DLS
        // Have to use aria-labelledby instead of aria-label because of a quirk in dls-react
        <Modal
            id={`${groupId}-categoryHistoryModal`}
            aria-labelledby={`${groupId}-categoryHistoryModalHeader`}
            data-cy={Selectors.SpendSmart.CATEGORY_HISTORY_MODAL}
            className={styles.modalContainer}
        >
            <ModalHeader
                className={styles.modalHeader}
                onClose={() => {
                    trackClick(location.pathname, adobeTags.CategoryBreakdown.Modal.Close);
                    closeModal();
                }}
                data-cy={Selectors.SpendSmart.CATEGORY_HISTORY_MODAL_TITLE}
                id={`${groupId}-categoryHistoryModalHeader`}
            >
                <div className={styles.modalHeaderSpacer}></div>
                <Typography variant={TypographyVariants.HEADING_XS} component="h1" className={styles.modalHeaderTitle}>
                    {LiteralsService.getLiteral('Budget.History.categoryModalHeader', [categoryName])}
                </Typography>
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
                <div className={styles.modalBodyFirstRow}>
                    <Output
                        title={LiteralsService.getLiteral('Budget.History.categoryModalOutputTitle')}
                        titleComponent="h2"
                        main={averageSpent}
                        mainIsLoading={isLoadingSpendingHistory}
                        tooltipText={LiteralsService.getLiteral('Tooltips.yourAverageMonthlySpending.body')}
                        tooltipPlacement={TooltipPlacement.BOTTOM}
                        onClick={() => trackClick(location.pathname, adobeTags.CategoryBreakdown.Modal.Tooltip)}
                        tooltipClassName={styles.moreInfoTooltip}
                    />
                    <Anchor
                        onClick={() => handleOpenTransactions(startDate, endDate)}
                        tag={adobeTags.CategoryBreakdown.Modal.ViewTransaction}
                        href={getCategoryTransactionsUrl(groupId, startDate, endDate)}
                        className={styles.linkIconText}
                        data-cy={Selectors.SpendSmart.VIEW_TRANSACTIONS_LINK}
                        inline
                        aria-label={LiteralsService.getLiteral('Budget.Current.viewTransactionsModalLinkDescription', [
                            categoryName
                        ])}
                    >
                        <IconActivity className={styles.linkIcon} />
                        {LiteralsService.getLiteral('Budget.Current.viewTransactionsForLastX', [12])}
                    </Anchor>
                </div>
                <SpendingHistoryBarChart
                    title={LiteralsService.getLiteral('Budget.Chart.categoryBarChartTitle')}
                    groupId={groupId}
                    handleOpenTransactions={handleOpenTransactions}
                    tagName={adobeTags.CategoryBreakdown.Modal.Graph}
                    inModal={true}
                    categoryName={categoryName}
                />
            </ModalBody>
        </Modal>
    );
}
